import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { UserPlus, Trash2, ShieldCheck, User as UserIcon, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function AdminPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "user" });
  const [confirmTarget, setConfirmTarget] = useState(null); // { id, email } or null
  const [pwdTarget, setPwdTarget] = useState(null); // { id, email } or null
  const [newPassword, setNewPassword] = useState("");
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const [pwdError, setPwdError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The lint rule `react-hooks/set-state-in-effect` reports a false positive
  // on this ordinary async submit handler — it is not called from any
  // useEffect. Suppressing the rule keeps the standard React event-handler
  // pattern intact.
  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/users", form);
      setUsers((prev) => [...prev, data]);
      toast.success(`${data.email} created`);
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "user" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      const { data } = await api.patch(`/admin/users/${id}`, { role: newRole });
      setUsers((prev) => prev.map((u) => (u.id === id ? data : u)));
      toast.success(`${data.email} is now ${data.role}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update role");
    }
  };

  const handleDelete = async (id, email) => {
    try {
      await api.delete(`/admin/users/${id}`);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      toast.success(`${email} deleted`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete user");
    } finally {
      setConfirmTarget(null);
    }
  };

  const closePwdDialog = () => {
    setPwdTarget(null);
    setNewPassword("");
    setPwdError("");
    setPwdSubmitting(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setPwdError("Password must be at least 6 characters.");
      return;
    }
    setPwdSubmitting(true);
    setPwdError("");
    try {
      await api.patch(`/admin/users/${pwdTarget.id}`, { password: newPassword });
      toast.success(`Password updated for ${pwdTarget.email}`);
      closePwdDialog();
    } catch (err) {
      setPwdError(
        formatApiErrorDetail(err.response?.data?.detail) || "Failed to update password",
      );
    } finally {
      setPwdSubmitting(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-8 max-w-5xl" data-testid="admin-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <p className="text-slate-600 text-sm leading-relaxed max-w-xl">
          Manage who can access the desk. Create new users, promote them to admin, or remove access.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-slate-900 hover:bg-slate-800 text-white gap-2"
              data-testid="add-user-button"
            >
              <UserPlus className="w-4 h-4" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" data-testid="add-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">Add New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label htmlFor="u-email" className="text-xs tracking-[0.15em] uppercase text-slate-600">
                  Email
                </Label>
                <Input
                  id="u-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="trader@example.com"
                  className="mt-1.5"
                  data-testid="user-email-input"
                />
              </div>
              <div>
                <Label htmlFor="u-name" className="text-xs tracking-[0.15em] uppercase text-slate-600">
                  Name
                </Label>
                <Input
                  id="u-name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Jane Doe"
                  className="mt-1.5"
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <Label htmlFor="u-pwd" className="text-xs tracking-[0.15em] uppercase text-slate-600">
                  Password
                </Label>
                <Input
                  id="u-pwd"
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={set("password")}
                  placeholder="At least 6 characters"
                  className="mt-1.5"
                  data-testid="user-password-input"
                />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-slate-600">Role</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}
                >
                  <SelectTrigger className="mt-1.5" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="guest" data-testid="role-option-guest">Guest</SelectItem>
                    <SelectItem value="user" data-testid="role-option-user">User</SelectItem>
                    <SelectItem value="admin" data-testid="role-option-admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  data-testid="cancel-user-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="submit-user-button"
                >
                  {submitting ? "Saving..." : "Create User"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div
        className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm"
        data-testid="users-table"
      >
        <table className="w-full min-w-[640px] table-auto">
          <thead>
            <tr className="bg-slate-50/80 border-b border-slate-200">
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Email
              </th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Name
              </th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[180px]">
                Role
              </th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[140px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="py-12 text-center text-sm text-slate-500">
                  Loading users...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-12 text-center text-sm text-slate-500">
                  No users.
                </td>
              </tr>
            ) : (
              users.map((u) => {
                const isSelf = u.id === me?.id;
                return (
                  <tr
                    key={u.id}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                    data-testid={`user-row-${u.email}`}
                  >
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        {u.role === "admin" ? (
                          <ShieldCheck className="w-4 h-4 text-slate-700" strokeWidth={1.75} />
                        ) : (
                          <UserIcon className="w-4 h-4 text-slate-400" strokeWidth={1.75} />
                        )}
                        <span className="text-sm font-medium text-slate-900">{u.email}</span>
                        {isSelf && (
                          <span className="text-[10px] tracking-[0.15em] uppercase text-slate-400 ml-1">
                            (You)
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-sm text-slate-600">{u.name || "—"}</td>
                    <td className="py-4 px-6">
                      <Select
                        value={u.role}
                        onValueChange={(v) => handleRoleChange(u.id, v)}
                        disabled={isSelf}
                      >
                        <SelectTrigger
                          className="w-[140px]"
                          data-testid={`role-select-${u.email}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="guest">Guest</SelectItem>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-4 px-6 text-right w-[140px]">
                      <div className="inline-flex items-center gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            setPwdTarget({ id: u.id, email: u.email });
                            setNewPassword("");
                            setPwdError("");
                          }}
                          aria-label={`Reset password for ${u.email}`}
                          title={`Reset password for ${u.email}`}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 transition-colors"
                          data-testid={`reset-password-${u.email}`}
                        >
                          <KeyRound className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmTarget({ id: u.id, email: u.email })}
                          aria-label={`Remove ${u.email}`}
                          title={isSelf ? "You can't delete yourself" : `Remove ${u.email}`}
                          disabled={isSelf}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-slate-500 disabled:hover:border-slate-200 transition-colors"
                          data-testid={`delete-user-${u.email}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        open={pwdTarget !== null}
        onOpenChange={(o) => !o && closePwdDialog()}
      >
        <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              Reset password
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="text-sm text-slate-600">
              Set a new password for{" "}
              <span className="font-mono text-slate-900">{pwdTarget?.email}</span>
              . They will need to use this new password the next time they log in.
            </div>
            <div>
              <Label
                htmlFor="rp-new"
                className="text-xs tracking-[0.15em] uppercase text-slate-600"
              >
                New password
              </Label>
              <Input
                id="rp-new"
                type="password"
                required
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                className="mt-1.5"
                placeholder="At least 6 characters"
                autoFocus
                data-testid="rp-new-input"
              />
            </div>
            {pwdError && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                data-testid="rp-error"
              >
                {pwdError}
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closePwdDialog}
                disabled={pwdSubmitting}
                data-testid="rp-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={pwdSubmitting}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="rp-submit-button"
              >
                {pwdSubmitting ? "Saving..." : "Update password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmTarget !== null}
        onOpenChange={(o) => !o && setConfirmTarget(null)}
      >
        <AlertDialogContent data-testid="delete-user-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{" "}
              <span className="font-mono text-slate-800">{confirmTarget?.email}</span>{" "}
              from the desk. They will lose access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-user">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                confirmTarget && handleDelete(confirmTarget.id, confirmTarget.email)
              }
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-user"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
